import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Form,
  FormGroup,
  FormFeedback,
  Input,
  Label,
  Row,
  Col,
} from "reactstrap";

import { provincesRepo } from "data/repositories/provincesRepo";
import { adminThemes, provinceIds } from "data/admin/domain";
import { seasonLabel, themeLabel } from "labels/tr";
import { adminPaths } from "admin/paths";
import { normalizeSlug, seasonOptions } from "admin/contentUtils";
import AdminFormActions from "components/admin/AdminFormActions";
import AdminPageHeader from "components/admin/AdminPageHeader";
import RepeaterField from "components/admin/RepeaterField";

const bestSeasonOptions = [
  ...seasonOptions.map((value) => ({ value, label: seasonLabel(value) })),
  { value: "All", label: "Tüm sezonlar" },
];

const themeSelectOptions = adminThemes.map((value) => ({
  value,
  label: themeLabel(value),
}));

const highlightFields = [
  { key: "title", label: "Başlık", placeholder: "Sümela Manastırı", col: 6 },
  {
    key: "theme",
    label: "Tema",
    type: "select",
    options: themeSelectOptions,
    col: 6,
  },
  {
    key: "shortDescription",
    label: "Kısa açıklama",
    type: "textarea",
    rows: 2,
    placeholder: "Kartta görünen kısa metin",
    col: 12,
  },
  { key: "locationHint", label: "Konum", placeholder: "Maçka", col: 6 },
  {
    key: "bestSeason",
    label: "En iyi sezon",
    type: "select",
    options: bestSeasonOptions,
    col: 6,
  },
  {
    key: "imageUrl",
    label: "Görsel URL",
    placeholder: "https://...",
    col: 12,
  },
];

function createHighlight(prefix) {
  return () => ({
    id: `${prefix}-${Math.random().toString(16).slice(2, 8)}`,
    title: "",
    shortDescription: "",
    theme: "Nature",
    locationHint: "",
    bestSeason: "Summer",
    imageUrl: "",
  });
}

function emptyProvince() {
  return {
    id: "",
    name: "",
    slogan: "",
    story: "",
    history: "",
    heroImage: "",
    historyCoverImage: "",
    landmark: "",
    foods: "",
    themes: [],
    highlights: [],
    experiences: [],
    seasons: [],
  };
}

function mapProvinceToForm(current) {
  return {
    id: current.id || "",
    name: current.name || "",
    slogan: current.slogan || "",
    story: current.story || "",
    history: current.history || "",
    heroImage: current.heroImage || "",
    historyCoverImage: current.historyCoverImage || "",
    landmark: current.landmark || "",
    foods: Array.isArray(current.foods) ? current.foods.join(", ") : "",
    themes: current.themes || [],
    highlights: Array.isArray(current.highlights)
      ? current.highlights.map((item) => ({
          ...item,
          imageUrl: item.imageUrl || "",
        }))
      : [],
    experiences: Array.isArray(current.experiences)
      ? current.experiences.map((item) => ({
          ...item,
          imageUrl: item.imageUrl || "",
        }))
      : [],
    seasons: current.seasons || [],
  };
}

function validateForm(form, isNew, provinceId) {
  const nextErrors = {};
  const normalizedId = normalizeSlug(form.id);

  if (!normalizedId) nextErrors.id = "Şehir kimliği zorunludur.";
  else if (!provinceIds.includes(normalizedId)) nextErrors.id = "Kimlik mobil uygulamadaki şehir listesinde bulunmalı.";
  else if (isNew && provincesRepo.getById(normalizedId)) nextErrors.id = "Bu ID ile kayıt zaten var.";
  else if (!isNew && normalizedId !== provinceId) nextErrors.id = "Var olan kayıt için ID değiştirilemez.";

  if (!form.name.trim()) nextErrors.name = "Şehir adı zorunludur.";
  if (!form.slogan.trim()) nextErrors.slogan = "Slogan zorunludur.";
  if (!form.story.trim()) nextErrors.story = "Tanıtım (hikaye) alanı zorunludur.";
  if (!form.history.trim()) nextErrors.history = "Tarihçe alanı zorunludur.";
  if (!form.themes.length) nextErrors.themes = "En az bir tema seçin.";
  if (!form.seasons.length) nextErrors.seasons = "En az bir sezon seçin.";

  const incompleteHighlight = form.highlights.find((item) => !String(item?.title || "").trim());
  if (incompleteHighlight) nextErrors.highlights = "Her ziyaret noktasının başlığı olmalı.";

  const incompleteExperience = form.experiences.find((item) => !String(item?.title || "").trim());
  if (incompleteExperience) nextErrors.experiences = "Her deneyimin başlığı olmalı.";

  return { nextErrors, normalizedId };
}

export default function ProvinceEdit() {
  const navigate = useNavigate();
  const params = useParams();
  const provinceId = params.id;
  const isNew = provinceId === "new" || !provinceId;

  const [form, setForm] = useState(emptyProvince());
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const readOnlyId = !isNew;
  const title = isNew ? "Yeni şehir" : `Şehir düzenle: ${provinceId}`;

  useEffect(() => {
    setError("");
    if (isNew) {
      setForm(emptyProvince());
      setErrors({});
      return;
    }

    const current = provincesRepo.getById(provinceId);
    if (!current) {
      setError("Şehir bulunamadı");
      return;
    }
    setForm(mapProvinceToForm(current));
    setErrors({});
  }, [isNew, provinceId]);

  const selectedThemes = useMemo(() => new Set(form.themes || []), [form.themes]);

  const toggleTheme = (t) => {
    const next = new Set(selectedThemes);
    if (next.has(t)) next.delete(t);
    else next.add(t);
    setForm((prev) => ({ ...prev, themes: Array.from(next) }));
  };

  const onSave = (e) => {
    e.preventDefault();
    setError("");
    const { nextErrors, normalizedId } = validateForm(form, isNew, provinceId);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      return;
    }

    const payload = {
      id: normalizedId,
      name: form.name.trim(),
      slogan: form.slogan.trim(),
      story: form.story.trim(),
      history: form.history.trim(),
      heroImage: form.heroImage.trim() || null,
      historyCoverImage: form.historyCoverImage.trim() || null,
      landmark: form.landmark.trim(),
      foods: String(form.foods || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
      themes: form.themes,
      highlights: form.highlights
        .filter((item) => String(item?.title || "").trim())
        .map((item) => ({
          ...item,
          title: String(item.title).trim(),
          shortDescription: String(item.shortDescription || "").trim(),
          locationHint: String(item.locationHint || "").trim(),
          imageUrl: String(item.imageUrl || "").trim() || null,
        })),
      experiences: form.experiences
        .filter((item) => String(item?.title || "").trim())
        .map((item) => ({
          ...item,
          title: String(item.title).trim(),
          shortDescription: String(item.shortDescription || "").trim(),
          locationHint: String(item.locationHint || "").trim(),
          imageUrl: String(item.imageUrl || "").trim() || null,
        })),
      seasons: form.seasons,
    };

    try {
      setSaving(true);
      if (isNew) {
        provincesRepo.create(payload);
      } else {
        provincesRepo.update(form.id, payload);
      }

      navigate(`/admin${adminPaths.content.provinces.list}`, {
        state: {
          status: {
            color: "success",
            message: `${payload.name} başarıyla ${isNew ? "oluşturuldu" : "güncellendi"}.`,
          },
        },
      });
    } catch (err) {
      setError(err?.message || "Kaydetme hatası");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AdminPageHeader
        gradient="info"
        title={title}
        description="Mobil şehir detayıyla aynı alanlar: tanıtım, tarihçe, ziyaret noktaları, deneyimler ve görseller."
        backAction={() => navigate(`/admin${adminPaths.content.provinces.list}`)}
      />

      <Container className="mt--6" fluid>
        <Form onSubmit={onSave}>
          <Row>
            <Col xl="8">
              <Card className="shadow mb-4">
                <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                  <h3 className="mb-0">Temel bilgiler</h3>
                  {!isNew ? (
                    <Button
                      tag={Link}
                      to={`/admin${adminPaths.content.provinces.detail(provinceId)}`}
                      color="secondary"
                      outline
                      size="sm"
                    >
                      Önizle
                    </Button>
                  ) : null}
                </CardHeader>
                <CardBody>
                  {error ? <Alert color="danger">{error}</Alert> : null}

                  <FormGroup>
                    <Label>ID (slug)</Label>
                    <Input
                      value={form.id}
                      onChange={(e) => setForm((p) => ({ ...p, id: normalizeSlug(e.target.value) }))}
                      placeholder="ör: trabzon"
                      disabled={readOnlyId}
                      invalid={Boolean(errors.id)}
                    />
                    <FormFeedback>{errors.id}</FormFeedback>
                    <div className="small text-muted mt-2">
                      Geçerli ID örnekleri: {provinceIds.slice(0, 6).join(", ")}...
                    </div>
                  </FormGroup>

                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>Ad</Label>
                        <Input
                          value={form.name}
                          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                          placeholder="Trabzon"
                          invalid={Boolean(errors.name)}
                        />
                        <FormFeedback>{errors.name}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Slogan</Label>
                        <Input
                          value={form.slogan}
                          onChange={(e) => setForm((p) => ({ ...p, slogan: e.target.value }))}
                          placeholder="Kısa slogan"
                          invalid={Boolean(errors.slogan)}
                        />
                        <FormFeedback>{errors.slogan}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label>Tanıtım (mobil: Hikaye)</Label>
                    <Input
                      type="textarea"
                      rows="5"
                      value={form.story}
                      onChange={(e) => setForm((p) => ({ ...p, story: e.target.value }))}
                      placeholder="Şehir tanıtım metni"
                      invalid={Boolean(errors.story)}
                    />
                    <FormFeedback>{errors.story}</FormFeedback>
                  </FormGroup>

                  <FormGroup>
                    <Label>Tarihçe</Label>
                    <Input
                      type="textarea"
                      rows="5"
                      value={form.history}
                      onChange={(e) => setForm((p) => ({ ...p, history: e.target.value }))}
                      placeholder="Şehrin tarihçesi"
                      invalid={Boolean(errors.history)}
                    />
                    <FormFeedback>{errors.history}</FormFeedback>
                  </FormGroup>

                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>Kapak / hero görseli URL</Label>
                        <Input
                          value={form.heroImage}
                          onChange={(e) => setForm((p) => ({ ...p, heroImage: e.target.value }))}
                          placeholder="https://..."
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Tarihçe kapak görseli URL</Label>
                        <Input
                          value={form.historyCoverImage}
                          onChange={(e) => setForm((p) => ({ ...p, historyCoverImage: e.target.value }))}
                          placeholder="https://..."
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>Öne çıkan yer (landmark)</Label>
                        <Input
                          value={form.landmark}
                          onChange={(e) => setForm((p) => ({ ...p, landmark: e.target.value }))}
                          placeholder="Uzungöl"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Yerel lezzetler</Label>
                        <Input
                          value={form.foods}
                          onChange={(e) => setForm((p) => ({ ...p, foods: e.target.value }))}
                          placeholder="Kuymak, Akçaabat köftesi"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="mb-3">
                    <Label className="d-block">Temalar</Label>
                    <Row>
                      {adminThemes.map((t) => (
                        <Col md="4" key={t} className="mb-2">
                          <div className="custom-control custom-checkbox">
                            <input
                              className="custom-control-input"
                              id={`theme-${t}`}
                              type="checkbox"
                              checked={selectedThemes.has(t)}
                              onChange={() => toggleTheme(t)}
                            />
                            <label className="custom-control-label" htmlFor={`theme-${t}`}>
                              {themeLabel(t)}
                            </label>
                          </div>
                        </Col>
                      ))}
                    </Row>
                    {errors.themes ? <div className="small text-danger">{errors.themes}</div> : null}
                  </div>

                  <div className="mb-4">
                    <Label className="d-block">Sezonlar</Label>
                    {seasonOptions.map((season) => (
                      <Button
                        key={season}
                        type="button"
                        color={form.seasons.includes(season) ? "info" : "secondary"}
                        outline
                        className="mr-2 mb-2"
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            seasons: prev.seasons.includes(season)
                              ? prev.seasons.filter((item) => item !== season)
                              : [...prev.seasons, season],
                          }))
                        }
                      >
                        {seasonLabel(season)}
                      </Button>
                    ))}
                    {errors.seasons ? <div className="small text-danger">{errors.seasons}</div> : null}
                  </div>

                  {isNew ? null : (
                    <div className="mb-3">
                      <Label className="d-block">Durum</Label>
                      <Badge color={provincesRepo.getById(provinceId)?.deletedAt ? "warning" : "success"} pill>
                        {provincesRepo.getById(provinceId)?.deletedAt ? "Arşivde" : "Aktif"}
                      </Badge>
                    </div>
                  )}

                  <AdminFormActions saving={saving} onCancel={() => navigate(`/admin${adminPaths.content.provinces.list}`)} />
                </CardBody>
              </Card>
            </Col>

            <Col xl="4">
              <Card className="shadow mb-4">
                <CardHeader className="border-0">
                  <h3 className="mb-0">Mobil eşleme</h3>
                </CardHeader>
                <CardBody className="small text-muted">
                  <p className="mb-2">Bu formdaki alanlar mobil şehir detayındaki bölümlere karşılık gelir:</p>
                  <ul className="pl-3 mb-0">
                    <li>Tanıtım → story</li>
                    <li>Tarihçe → history + kapak görseli</li>
                    <li>Ziyaret edilecek yerler → highlights</li>
                    <li>Deneyimler → experiences</li>
                    <li>Fırsatlar ayrı menüde yönetilir</li>
                  </ul>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <RepeaterField
            title="Ziyaret edilecek yerler"
            description="Mobil detaydaki öne çıkan yer kartları (başlık, açıklama, tema, konum, sezon, görsel)."
            items={form.highlights}
            onChange={(items) => setForm((prev) => ({ ...prev, highlights: items }))}
            createItem={createHighlight(form.id ? `h-${form.id}` : "h-new")}
            fields={highlightFields}
            addLabel="Yer ekle"
            emptyMessage="Henüz ziyaret noktası yok."
          />
          {errors.highlights ? <div className="small text-danger mb-3">{errors.highlights}</div> : null}

          <RepeaterField
            title="Deneyimler"
            description="Mobil detaydaki deneyim kartları."
            items={form.experiences}
            onChange={(items) => setForm((prev) => ({ ...prev, experiences: items }))}
            createItem={createHighlight(form.id ? `e-${form.id}` : "e-new")}
            fields={highlightFields}
            addLabel="Deneyim ekle"
            emptyMessage="Henüz deneyim yok."
          />
          {errors.experiences ? <div className="small text-danger mb-3">{errors.experiences}</div> : null}

          <Card className="shadow mb-5">
            <CardBody>
              <AdminFormActions saving={saving} onCancel={() => navigate(`/admin${adminPaths.content.provinces.list}`)} />
            </CardBody>
          </Card>
        </Form>
      </Container>
    </>
  );
}
